import React, { useState, useCallback, useEffect } from "react";
import Moment from "moment";
import Icon from "../Icon/Icon";
import iconDocumentacion from "../../../img/icons/svg/historial/historial-card-05.svg";
import "./Cards.scss";

export interface IHealthcareDocumentProp {
  documents?: any[];
  handleRequestDocument: (documentID: string) => void;
  highlightId?: string | null;
}

const mimeToExt: Record<string, string> = {
  "application/pdf": ".pdf",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/tiff": ".tiff",
  "text/plain": ".txt",
  "application/zip": ".zip",
};

const resolveFilename = (nombre: string, uri: string, mimeType: string): string => {
  const hasExtension = /\.[a-zA-Z0-9]{1,5}$/.test(nombre);
  if (hasExtension) return nombre;
  const uriPath = uri.split("?")[0];
  const uriExt = uriPath.match(/(\.[a-zA-Z0-9]{1,5})$/)?.[1];
  if (uriExt) return `${nombre}${uriExt}`;
  const mimeExt = mimeToExt[mimeType.split(";")[0].trim()];
  return mimeExt ? `${nombre}${mimeExt}` : nombre;
};

const getExtension = (nombre: string): string => {
  const m = nombre?.match(/\.([a-zA-Z0-9]{1,5})$/);
  return m ? m[1].toUpperCase() : "";
};

const HealthcareDocumentsCards: React.FC<IHealthcareDocumentProp> = ({
  documents = [],
  handleRequestDocument,
  highlightId,
}) => {
  const [animating, setAnimating] = useState(false);

  // preserve original highlight pulse animation
  useEffect(() => {
    if (highlightId && documents.length > 0) {
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightId, documents.length]);

  const handleDownload = useCallback(async (uri: string, nombre: string) => {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = resolveFilename(nombre, uri, blob.type);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(uri, "_blank", "noopener,noreferrer");
    }
  }, []);

  if (!documents.length) {
    return <p className="cards-empty">No hay documentos para mostrar.</p>;
  }

  return (
    <div className={animating ? "highlight-cards-pulse" : ""}>
      <div className="cards-list">
        {documents.map((doc: any, i: number) => {
          const ext = getExtension(doc.nombre);
          return (
            <article key={doc.id || i} className="item-card item-card--compact">
              <div className="item-card__icon" style={{ background: '#4061ce' }}>
                <img src={iconDocumentacion} alt="documentación" style={{ width: 22, height: 22, objectFit: "contain" }} />
              </div>
              <div className="item-card__content">
                <div className="item-card__title">{doc.nombre}</div>
                <div className="item-card__meta">
                  {ext && <span className="item-card__sub">{ext}</span>}
                  {doc.company && doc.company !== "-" && (
                    <span className="item-card__sub">{doc.company}</span>
                  )}
                  {doc.fecha && (
                    <span className="item-card__sub">
                      {Moment(doc.fecha).format("DD MMM YYYY")}
                    </span>
                  )}
                </div>
              </div>
              <div className="item-card__action">
                <button
                  className="item-card__download-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (doc.requiereOtp === false) {
                      handleDownload(doc.uri, doc.nombre);
                    } else {
                      handleRequestDocument(doc.id);
                    }
                  }}
                  title={doc.requiereOtp === false ? "Descargar" : "Verificar y descargar"}
                >
                  <Icon name="download" size={14} />
                  {doc.requiereOtp === false ? "Descargar" : "Verificar"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default HealthcareDocumentsCards;
